/**
 * EndpointSelector Component
 *
 * Displays a dropdown/list of parsed endpoints from an OpenAPI specification.
 * Shows endpoint method, path, and summary. Handles endpoint selection and
 * supports initial endpoint pre-selection.
 *
 * Requirements:
 * - 1.1: Display all parsed endpoints
 * - 1.5: Show endpoints sorted by path then method
 * - 17.2: Parse and display endpoints when product is selected
 * - 19.1: Support initial endpoint pre-selection via prop
 * - 19.2: Display endpoint parameters and request body fields when selected
 * - 19.3: Display endpoint selection control without selection when no initial endpoint
 */

import { ParsedEndpoint } from '../types';
import { useEffect, useState } from 'react';

export interface EndpointSelectorProps {
  /** Array of parsed endpoints from OpenAPI specification */
  endpoints: ParsedEndpoint[];
  /** Currently selected endpoint */
  selectedEndpoint: ParsedEndpoint | null;
  /** Callback when endpoint selection changes */
  onSelect: (endpoint: ParsedEndpoint | null) => void;
  /** Optional initial endpoint ID to pre-select */
  initialEndpointId?: string;
  /** Whether the selector is disabled */
  disabled?: boolean;
}

/**
 * EndpointSelector component displays a dropdown of API endpoints.
 *
 * @param endpoints - Array of parsed endpoints
 * @param selectedEndpoint - Currently selected endpoint
 * @param onSelect - Callback function when selection changes
 * @param initialEndpointId - Optional endpoint ID to pre-select on mount
 * @param disabled - Whether the selector is disabled
 */
export const EndpointSelector = ({
  endpoints,
  selectedEndpoint,
  onSelect,
  initialEndpointId,
  disabled = false,
}: EndpointSelectorProps) => {
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Handle initial endpoint pre-selection (Requirement 19.1)
   */
  useEffect(() => {
    if (!isInitialized && initialEndpointId && endpoints.length > 0) {
      const initialEndpoint = endpoints.find((endpoint) => endpoint.id === initialEndpointId);

      if (initialEndpoint) {
        onSelect(initialEndpoint);
      }

      setIsInitialized(true);
    }
  }, [initialEndpointId, endpoints, isInitialized, onSelect]);

  /**
   * Handle endpoint selection change
   */
  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const endpointId = event.target.value;

    if (!endpointId) {
      onSelect(null);
      return;
    }

    const endpoint = endpoints.find((e) => e.id === endpointId);
    onSelect(endpoint || null);
  };

  /**
   * Get method badge color based on HTTP method
   */
  const getMethodColor = (method: string): string => {
    switch (method) {
      case 'GET':
        return 'bg-blue-100 text-blue-800';
      case 'POST':
        return 'bg-green-100 text-green-800';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'PATCH':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Format endpoint option text for display
   */
  const formatEndpointOption = (endpoint: ParsedEndpoint): string => {
    const summary = endpoint.summary ? ` - ${endpoint.summary}` : '';
    return `${endpoint.method} ${endpoint.path}${summary}`;
  };

  return (
    <div className="space-y-2">
      <label htmlFor="endpoint-selector" className="block text-sm font-medium text-gray-700">
        Select Endpoint
        <span className="text-red-500 ml-1" aria-label="required">
          *
        </span>
      </label>

      {/* Endpoint count indicator */}
      {endpoints.length > 0 && (
        <p className="text-xs text-gray-500">
          {endpoints.length} endpoint{endpoints.length !== 1 ? 's' : ''} available
        </p>
      )}

      {/* No endpoints message */}
      {endpoints.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
          <p className="text-sm text-yellow-800">
            No endpoints available. Please select a product with an OpenAPI specification.
          </p>
        </div>
      )}

      {/* Endpoint dropdown selector (Requirements 1.1, 1.5, 19.3) */}
      {endpoints.length > 0 && (
        <select
          id="endpoint-selector"
          value={selectedEndpoint?.id || ''}
          onChange={handleChange}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          aria-label="Select API endpoint"
        >
          <option value="">-- Select an endpoint --</option>
          {endpoints.map((endpoint) => (
            <option key={endpoint.id} value={endpoint.id}>
              {formatEndpointOption(endpoint)}
            </option>
          ))}
        </select>
      )}

      {/* Selected endpoint details (Requirement 19.2) */}
      {selectedEndpoint && (
        <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md space-y-3">
          {/* Method and path */}
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-xs font-semibold rounded ${getMethodColor(
                selectedEndpoint.method
              )}`}
            >
              {selectedEndpoint.method}
            </span>
            <code className="text-sm font-mono text-gray-800">{selectedEndpoint.path}</code>
          </div>

          {/* Summary */}
          {selectedEndpoint.summary && (
            <p className="text-sm text-gray-700 font-medium">{selectedEndpoint.summary}</p>
          )}

          {/* Description */}
          {selectedEndpoint.description && (
            <p className="text-sm text-gray-600">{selectedEndpoint.description}</p>
          )}

          {/* Operation ID */}
          {selectedEndpoint.operationId && (
            <p className="text-xs text-gray-500">
              <span className="font-medium">Operation ID:</span> {selectedEndpoint.operationId}
            </p>
          )}

          {/* Parameters summary */}
          {selectedEndpoint.parameters.length > 0 && (
            <div className="text-xs text-gray-600">
              <span className="font-medium">Parameters:</span> {selectedEndpoint.parameters.length}{' '}
              parameter
              {selectedEndpoint.parameters.length !== 1 ? 's' : ''} (
              {selectedEndpoint.parameters.filter((p) => p.in === 'path').length} path,{' '}
              {selectedEndpoint.parameters.filter((p) => p.in === 'query').length} query)
            </div>
          )}

          {/* Request body indicator */}
          {selectedEndpoint.requiresBody && (
            <div className="text-xs text-gray-600">
              <span className="font-medium">Request Body:</span> Required (JSON)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EndpointSelector;
